import { z } from 'zod';

/**
 * Validates a website URL with the following rules:
 * - Must be a valid URL format
 * - Must have at least 2 domain labels (domain + TLD)
 * - If only 2 labels (e.g., "example.com"), domain must be at least 4 characters
 *   This rejects short domains like "www.com", "abc.com", "a.co"
 * - No trailing dots or consecutive dots
 * - Scheme (http/https) is optional and will be normalized to https for validation
 *
 * @param value - The URL string to validate
 * @returns true if valid, false otherwise
 *
 * @example
 * isValidWebsiteUrl('example.com') // true
 * isValidWebsiteUrl('https://example.com') // true
 * isValidWebsiteUrl('www.example.com') // true
 * isValidWebsiteUrl('www.com') // false (domain too short)
 * isValidWebsiteUrl('example.com.') // false (trailing dot)
 * isValidWebsiteUrl('example..com') // false (consecutive dots)
 */
export function isValidWebsiteUrl(value: string): boolean {
  if (!value || value.endsWith('.') || value.includes('..')) {
    return false;
  }

  // Normalize URL by adding scheme if missing for validation
  const normalizedValue = /^https?:\/\//i.test(value) ? value : `https://${value}`;

  // Validate URL format using Zod
  const urlSchema = z.string().url();
  const result = urlSchema.safeParse(normalizedValue);

  if (!result.success) {
    return false;
  }

  // Extract hostname for additional validation
  const url = new URL(normalizedValue);
  const labels = url.hostname.split('.');

  // Must have at least 2 labels (domain + TLD)
  if (labels.length < 2) {
    return false;
  }

  // Each label must be non-empty
  if (labels.some(label => label.length === 0)) {
    return false;
  }

  // If only 2 labels (e.g., "example.com"), the domain part must be at least 4 characters
  // This rejects "www.com" (3 chars), "abc.com" (3 chars), "a.co", etc.
  if (labels.length === 2 && (labels[0]?.length ?? 0) < 4) {
    return false;
  }

  return true;
}
